import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import { URL_CONTACTUS, URL_SIGNOUT, URL_SIGNIN, URL_ACCOUNT_REGISTRATION } from "@seek/adv-constants";
import { siteConfig, getLanguageFromLocale } from "@seek/melways-sites";
import { useTranslations } from "@vocab/react";
import { Box, Columns, Column, Text, IconChevron, IconLanguage, Stack, PageBlock, Divider, Actions, ButtonLink } from "braid-design-system";
import { baseMobileEvent, analyticsLanguageSelectorEvents, analyticsEvents, desktopAnlayticsEventToMobile, analyticsAccountDropDownEvents, analyticsMobileMenuEvents } from "../../../EmployerFooter/AuthenticatedFooterLinks/AuthenticatedFooterLinks.mjs";
import { useEmployerHeaderContext, testTags } from "../../EmployerHeader.mjs";
import { isAuthenticated } from "../../../EmployerFooter/EmployerFooter.mjs";
import { getCtaPath } from "../BrandedLogo/BrandedLogo.mjs";
import { translations } from "../../../../EmployerHeader/components/.vocab/index.mjs";
import { AccountInfo, AccountNavigation } from "../AccountDropdown/AccountDropdown.mjs";
import { CustomHeaderMobileLink } from "./HeaderLink.mjs";
import { vars } from "braid-design-system/css";
import { useState } from "react";
import { getWindowPathWithoutLanguage, languageMapping, getLanguagesForSelector } from "../HeaderActions/HeaderActions.mjs";
import { container } from "./MobileNavigation.css.mjs";
const MobileAccordionNavigation = ({
  id,
  label,
  children,
  active = false,
  onToggle
}) => {
  const [isOpen, setIsOpen] = useState(active);
  return /* @__PURE__ */ jsxs(Box, { children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        component: "button",
        display: "block",
        width: "full",
        textAlign: "left",
        paddingY: "small",
        cursor: "pointer",
        "aria-controls": id,
        "aria-expanded": isOpen,
        onClick: () => {
          setIsOpen(!isOpen);
          onToggle(!isOpen);
        },
        style: {
          minHeight: vars.touchableSize
        },
        children: /* @__PURE__ */ jsxs(Columns, { component: "span", space: "large", children: [
          /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(Text, { weight: active ? "strong" : "regular", children: label }) }),
          /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(IconChevron, { direction: isOpen ? "up" : "down" }) }) })
        ] })
      }
    ),
    /* @__PURE__ */ jsx(Box, { id, display: isOpen ? "block" : "none", children })
  ] });
};
const MobileLanguageSelector = ({
  language,
  site,
  linkRenderer,
  languages,
  urlResolver
}) => {
  const { handleInteractionTracking } = useEmployerHeaderContext();
  if (JSON.stringify(siteConfig[site].languages) !== JSON.stringify(languages)) {
    return null;
  }
  const strippedPath = getWindowPathWithoutLanguage();
  const languageOptions = languages.filter((l) => l !== language);
  const handleToggle = (expanded) => {
    const event = expanded ? analyticsLanguageSelectorEvents.toggleOpen : analyticsLanguageSelectorEvents.toggleClosed;
    const mobileEvent = {
      ...event,
      ...baseMobileEvent
    };
    handleInteractionTracking(mobileEvent);
  };
  return /* @__PURE__ */ jsx(Box, { "data-testid": "mobileLanguageSelector", children: /* @__PURE__ */ jsxs(Columns, { space: "xsmall", alignY: "top", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
      Box,
      {
        display: "flex",
        alignItems: "center",
        height: "touchable",
        width: "full",
        children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsx(IconLanguage, {}) })
      }
    ) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsx(
      MobileAccordionNavigation,
      {
        label: languageMapping[language],
        id: "mobile-language-selector-toggle",
        onToggle: handleToggle,
        children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: languageOptions.map((languageOption) => {
          const href = urlResolver({
            language: languageOption,
            path: strippedPath
          });
          return /* @__PURE__ */ jsx(
            CustomHeaderMobileLink,
            {
              href,
              linkRenderer,
              "data-testid": "mobile-language-menu-item",
              "data-analytics": analyticsEvents.trackSelectLanguage(
                true,
                languageOption
              ),
              onClick: () => {
                window.location.href = href;
              },
              analyticsEvent: {
                ...analyticsLanguageSelectorEvents.languageSelected,
                ...baseMobileEvent,
                actionPressed: `language selected - ${languageOption}`
              },
              children: languageMapping[languageOption]
            },
            languageOption
          );
        }) })
      }
    ) })
  ] }) });
};
const MobileSubNavigation = ({
  activeSubTabId,
  items,
  onMenuItemClick,
  linkRenderer
}) => /* @__PURE__ */ jsx(Stack, { space: "none", children: items.map(({ id, label, isBeta, ...subHeaderNavItemProps }, index) => /* @__PURE__ */ jsx(
  CustomHeaderMobileLink,
  {
    "data-analytics": subHeaderNavItemProps["data-analytics"],
    "data-test": subHeaderNavItemProps["data-test"],
    href: subHeaderNavItemProps.href,
    onClick: onMenuItemClick,
    active: id === activeSubTabId,
    linkRenderer,
    isNested: true,
    isBeta,
    analyticsEvent: desktopAnlayticsEventToMobile(
      subHeaderNavItemProps.analyticsEvent
    ),
    children: label
  },
  `mobile-nav-subitem-${index}`
)) });
const MobileNavigation = ({
  activeTabId,
  activeSubTabId,
  items,
  onCloseMobileMenu,
  linkRenderer: LinkRenderer
}) => {
  const {
    basic,
    hasError,
    isAgency,
    authenticationStatus,
    shouldDisplayActions,
    shouldDisplayCtaButton,
    site,
    locale,
    urlResolver,
    handleInteractionTracking
  } = useEmployerHeaderContext();
  const { t } = useTranslations(translations);
  const identityIsAuthenticated = isAuthenticated(authenticationStatus);
  const shouldDisplayAccountMenu = identityIsAuthenticated && !basic;
  const shouldDisplaySignOutLink = identityIsAuthenticated;
  const shouldDisplayAccountInfo = identityIsAuthenticated && !hasError;
  const shouldDisplayNavItems = !(basic || isAgency);
  const ctaURL = urlResolver({
    path: getCtaPath(authenticationStatus)
  });
  const contactURL = urlResolver({ path: URL_CONTACTUS });
  const signOutUrl = urlResolver({ path: URL_SIGNOUT });
  const signInUrl = urlResolver({ path: URL_SIGNIN });
  const registerUrl = urlResolver({ path: URL_ACCOUNT_REGISTRATION });
  const languagesForSelector = getLanguagesForSelector(site);
  const handleToggle = (analyticsEvent) => (expanded) => {
    const state = expanded ? "opened" : "closed";
    handleInteractionTracking({
      ...baseMobileEvent,
      actionPressed: `${analyticsEvent == null ? void 0 : analyticsEvent.actionPressed} - ${state}`
    });
  };
  const handleRegisterClick = () => {
    onCloseMobileMenu();
    handleInteractionTracking(analyticsMobileMenuEvents.register);
  };
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "section",
      id: "employer-header-mobile-navigation",
      background: "surface",
      className: container,
      zIndex: "modal",
      paddingY: "medium",
      children: /* @__PURE__ */ jsx(PageBlock, { width: "full", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
        shouldDisplayAccountInfo && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Box, { paddingBottom: "small", children: /* @__PURE__ */ jsx(
            AccountInfo,
            {
              onLinkClick: () => {
                handleInteractionTracking({
                  ...analyticsAccountDropDownEvents.switchAccount,
                  ...baseMobileEvent
                });
                onCloseMobileMenu();
              }
            }
          ) }),
          /* @__PURE__ */ jsx(Divider, {})
        ] }),
        shouldDisplayNavItems && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx("nav", { role: "navigation", children: items.map(
            ({
              label,
              id,
              subItems,
              href,
              rel,
              ...headerNavItemProps
            }) => {
              const hasSubItems = subItems && subItems.length > 0;
              const isActive = id === activeTabId;
              if (hasSubItems) {
                return /* @__PURE__ */ jsx(
                  MobileAccordionNavigation,
                  {
                    id,
                    label,
                    active: isActive,
                    onToggle: handleToggle(
                      headerNavItemProps.analyticsEvent
                    ),
                    children: /* @__PURE__ */ jsx(
                      MobileSubNavigation,
                      {
                        activeSubTabId,
                        items: subItems,
                        onMenuItemClick: onCloseMobileMenu,
                        linkRenderer: LinkRenderer
                      }
                    )
                  },
                  `mobile-nav-item-${id}`
                );
              }
              return /* @__PURE__ */ jsx(
                CustomHeaderMobileLink,
                {
                  "data-analytics": headerNavItemProps["data-analytics"],
                  "data-test": headerNavItemProps["data-test"],
                  href,
                  active: isActive,
                  linkRenderer: LinkRenderer,
                  onClick: onCloseMobileMenu,
                  rel,
                  analyticsEvent: desktopAnlayticsEventToMobile(
                    headerNavItemProps.analyticsEvent
                  ),
                  children: label
                },
                `mobile-nav-item-${id}`
              );
            }
          ) }),
          /* @__PURE__ */ jsx(Divider, {})
        ] }),
        languagesForSelector && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(
            MobileLanguageSelector,
            {
              site,
              language: getLanguageFromLocale(locale),
              linkRenderer: LinkRenderer,
              languages: languagesForSelector,
              urlResolver
            }
          ),
          /* @__PURE__ */ jsx(Divider, {})
        ] }),
        /* @__PURE__ */ jsxs(Stack, { space: "none", children: [
          shouldDisplayAccountMenu && /* @__PURE__ */ jsx(
            MobileAccordionNavigation,
            {
              id: "account-subnav-toggle",
              label: t("Account"),
              active: activeTabId === "account",
              onToggle: handleToggle(analyticsMobileMenuEvents.accountToggle),
              children: /* @__PURE__ */ jsx(
                AccountNavigation,
                {
                  variant: "mobile",
                  activeSubTabId,
                  linkRenderer: LinkRenderer,
                  onMenuItemClick: onCloseMobileMenu
                }
              )
            }
          ),
          shouldDisplayCtaButton && /* @__PURE__ */ jsx(
            CustomHeaderMobileLink,
            {
              href: ctaURL,
              onClick: onCloseMobileMenu,
              linkRenderer: LinkRenderer,
              "data-analytics": analyticsEvents.TRACK_CREATE_JOB,
              analyticsEvent: analyticsMobileMenuEvents.createAJobAd,
              children: t("Create a job ad")
            }
          ),
          /* @__PURE__ */ jsx(
            CustomHeaderMobileLink,
            {
              href: contactURL,
              linkRenderer: LinkRenderer,
              onClick: onCloseMobileMenu,
              "data-analytics": analyticsEvents.TRACK_CONTACT_US,
              analyticsEvent: analyticsMobileMenuEvents.contactUs,
              children: t("Contact us")
            }
          )
        ] }),
        /* @__PURE__ */ jsx(Divider, {}),
        /* @__PURE__ */ jsxs(Box, { children: [
          shouldDisplayActions && /* @__PURE__ */ jsxs(Actions, { children: [
            /* @__PURE__ */ jsx(
              ButtonLink,
              {
                size: "small",
                href: signInUrl,
                data: {
                  analytics: analyticsEvents.TRACK_SIGN_IN,
                  testid: testTags.SIGN_IN
                },
                onClick: () => {
                  handleInteractionTracking(analyticsMobileMenuEvents.signIn);
                },
                children: t("Sign in")
              }
            ),
            /* @__PURE__ */ jsx(
              ButtonLink,
              {
                variant: "transparent",
                size: "small",
                href: registerUrl,
                data: {
                  analytics: analyticsEvents.TRACK_REGISTRATION,
                  testid: testTags.REGISTER
                },
                onClick: handleRegisterClick,
                children: t("Register")
              }
            )
          ] }),
          shouldDisplaySignOutLink && /* @__PURE__ */ jsx(
            CustomHeaderMobileLink,
            {
              href: signOutUrl,
              onClick: onCloseMobileMenu,
              "data-analytics": analyticsEvents.TRACK_SIGN_OUT,
              "data-test": testTags.SIGN_OUT,
              linkRenderer: LinkRenderer,
              analyticsEvent: analyticsMobileMenuEvents.signOut,
              children: t("Sign out")
            }
          )
        ] })
      ] }) })
    }
  );
};
export {
  MobileNavigation
};
